import debug from "debug";
import * as THREE from "three";
import { KTX2Loader } from "three/examples/jsm/loaders/KTX2Loader.js";
const log = debug("app:makegeom");



/**
 * Manages the loading of textures, including support for KTX2 compressed textures, SVGs, and a fallback texture.
 * @category Experience
 * @subcategory Utils
 */
class TextureLoader {
  /**
   * Initializes the TextureLoader with the required renderer for KTX2Loader setup.
   * @param {THREE.WebGLRenderer} renderer - The WebGL renderer used to detect KTX2 support.
   */
  constructor(renderer) {
    this.loader = new THREE.TextureLoader();
    this.ktx2Loader = new KTX2Loader();

    // Set the correct path to the transcoder files
    this.ktx2Loader.setTranscoderPath("/assets/basis/");
    this.ktx2Loader.detectSupport(renderer);

    // Fallback SVG data
    this.fallbackSVG = new Blob(
      [
        `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100">
        <rect width="100" height="100" fill="lightgrey" />
        <text x="10" y="50" font-size="12" fill="red">Missing Image</text>
      </svg>`,
      ],
      { type: "image/svg+xml" }
    );
    this.fallbackSVGUrl = URL.createObjectURL(this.fallbackSVG);
  }

  cleanup() {
    console.log("Cleaning up TextureLoader: Disposing textures and releasing resources.");

    // Dispose of textures in the KTX2 loader
    this.ktx2Loader.dispose();

    // Revoke the object URL for the fallback SVG to free memory
    if (this.fallbackSVGUrl) {
        URL.revokeObjectURL(this.fallbackSVGUrl);
        this.fallbackSVGUrl = null;
    }

    console.log("TextureLoader cleanup complete.");
}

  /**
   * Loads a texture from a specified URL and returns a promise resolving to the texture.
   * Supports KTX2, SVG, and fallback texture types.
   * @param {Object} options - Configuration for loading the texture.
   * @param {string} options.type - The type of texture ("ktx2", "svg", or "fallback").
   * @param {string} options.key - The unique key or identifier for the texture.
   * @param {string} options.url - The URL of the texture to load.
   * @returns {Promise<{key: string, texture: THREE.Texture}>} A promise resolving to the loaded texture and its key.
   * @throws {Error} If the texture fails to load or is of an unsupported type.
   */
  async loadTexture({ type, key, url }) {
    return new Promise((resolve, reject) => {
      const onLoad = (texture) => {
        if (texture.image.width > 0 && texture.image.height > 0) {
          texture.colorSpace = THREE.SRGBColorSpace;
          texture.generateMipmaps = true;
          texture.anisotropy = 16;
          texture.minFilter = THREE.LinearMipMapLinearFilter;
          resolve({ key, texture });
        } else {
          reject(
            new Error(
              `Invalid texture dimensions: ${texture.image.width}x${texture.image.height}`
            )
          );
        }
      };

      const onError = () => {
        reject(new Error(`Failed to load ${type} texture from ${url}`));
      };

      if (type === "ktx2") {
        log("ktx2 loading", url);
        this.ktx2Loader.load(url, onLoad, undefined, onError);
      } else if (type === "svg" || type === "fallback") {
        const loadUrl = type === "fallback" ? this.fallbackSVGUrl : url;
        log("svg url for loading", loadUrl);
        this.loader.load(loadUrl, onLoad, undefined, onError);
      } else {
        reject(new Error("Unsupported texture type"));
      }
    });
  }
}

export default TextureLoader;
